import { inject, type Provider } from '@angular/core';
import { Router } from '@angular/router';
import {
  PAPER_ASK_CONFIRM_WIDGET,
  paperAskHostFromPanel,
  providePaperAskConversation,
  providePaperAskDefaultHost,
  providePaperAskEntityKinds,
  providePaperAskRouter,
  providePaperAskWidgets,
} from '@ricoh-paper/angular/ask';

import { FeatureFlagsViewerService } from '../../core/services/feature-flags-viewer.service';
import { SettingsAskConversation } from './settings-ask-conversation.service';
import { settingsAskEntityKinds } from './settings-ask-links';
import { SettingsAskPanel } from './settings-ask-panel';
import { SETTINGS_ASK_POLICY_DRAFT_WIDGET } from './policy-draft-widget.component';

/** Whether a URL is one of the two pages that ARE Ask: the Overview and `/ask`. */
export const isAskPage = (url: string): boolean => /^\/(ask|overview)(?:[/?#]|$)/.test(url);

/**
 * Ask in Settings, for the app's providers (Paper's `@ricoh-paper/angular/ask`):
 *
 *  - one conversation, `SettingsAskConversation`, over gemba-ai's `settings`
 *    app: the admin agent, and only it;
 *  - the confirm card (`actionConfirmation@1`): every change the agent makes
 *    waits for its Confirm;
 *  - the policy draft card (`policyDraft@1`): a policy drafted from words,
 *    opened in the Policies page's review, never applied by Ask;
 *  - the records an answer links (a page, a building, a space, a division, a
 *    campus, a role, a person), each opening its page in Settings;
 *  - the shell's push panel as the host Ask opens in;
 *  - the trigger router: Ask exists where the organisation has the admin AI
 *    capability on (`platform.admin-ai`); on the Overview and `/ask`, which
 *    are Ask, a door focuses the page's composer instead of opening a panel.
 */
export function provideSettingsAsk(): Provider[] {
  return [
    providePaperAskConversation(() => inject(SettingsAskConversation)),
    providePaperAskWidgets([PAPER_ASK_CONFIRM_WIDGET, SETTINGS_ASK_POLICY_DRAFT_WIDGET]),
    providePaperAskEntityKinds(() => settingsAskEntityKinds()),
    providePaperAskDefaultHost(() => paperAskHostFromPanel('shell', inject(SettingsAskPanel))),
    providePaperAskRouter(() => {
      const router = inject(Router);
      const flags = inject(FeatureFlagsViewerService);
      const conversation = inject(SettingsAskConversation);
      return {
        available: () => flags.is('platform.admin-ai'),
        // On a page that is Ask, a door puts the cursor in its composer
        // rather than opening the same thread a second time beside it.
        intercept: () => {
          if (!isAskPage(router.url)) return false;
          conversation.requestFocus();
          return true;
        },
        onFullPage: () => isAskPage(router.url),
        openFullPage: () => void router.navigateByUrl('/ask'),
      };
    }),
  ];
}
